import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"

// ** Third Party Imports
import { Link } from "react-router-dom"
import Skeleton from "react-loading-skeleton"
import { CloseCircle } from "iconsax-reactjs"
import { format, isToday, isYesterday } from "date-fns"
import { useInfiniteQuery } from "@tanstack/react-query"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Services
import { getNotificationListService } from "@pma/services/common"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Constants
import { notificationTabList } from "@pma/utils/constants"

// ** Routes
import { PROJECT_PAGE_ROUTE } from "@pma/utils/page-routes"

// ** Theme Components
import { Switch } from "@pma/components/common/switch"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@pma/components/common/sheet"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

interface NotificationDialogProps {
  notificationDialog: boolean
  setNotificationDialog: Dispatch<SetStateAction<boolean>>
  project_id?: string
  unreadCounts?: { total?: number; mentioned?: number; assigned?: number }
}

const unreadCountMap: Record<string, string> = {
  all: "total",
  mentioned: "mentioned",
  assigned: "assigned",
}

const NotificationDialog = ({
  notificationDialog,
  setNotificationDialog,
  project_id,
  unreadCounts,
}: NotificationDialogProps) => {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const sheetSide = isHebrew ? "left" : "right"
  const organization_id = authConfig?.last_organization_id

  const observerRef = useRef<HTMLDivElement | null>(null)

  const [activeTab, setActiveTab] = useState("all")
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [isOpen, setIsOpen] = useState(notificationDialog)

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ["get-notifications-list", activeTab, unreadOnly, project_id],
    queryFn: ({ pageParam = 1 }) =>
      getNotificationListService({
        organization_id,
        type: activeTab,
        paging_per_page: 20,
        paging_start_page: pageParam,
        only_unread: unreadOnly,
        ...(project_id && { project_id }),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.nextPage) return undefined
      return allPages.length + 1
    },
    refetchOnMount: "always",
    retry: 0,
  })

  const notificationList = useMemo(() => {
    const temp: any = []
    data?.pages.forEach((page) => {
      page.items.forEach((item: any) => {
        temp.push({
          id: item.id,
          title: item?.singe_action?._tasks?.title || "---",
          created_at: item?.created_at,
          task_id: item?.singe_action?._tasks?.id,
          project_id: item?.singe_action?._projects?.id,
          action: item?.singe_action?._action,
          _is_read: item?._is_read,
          by_user_name: item?.singe_action?._by_user?.name || item?.changes?._by_user?.name,
          by_user_picture: item?.singe_action?._by_user?.user_picture?.url || item?.changes?._by_user?.user_picture?.url,
          action_id: item?.singe_action?.action_id,
          action_en: item?.singe_action?._action?.en,
          action_he: item?.singe_action?._action?.he,
          change_type_en: item?.changes?._change_type?.en,
          change_type_he: item?.changes?._change_type?.he,
          project_title: item?.singe_action?._projects?.title,
          bill_id: item?.singe_action?.bill_id,
        })
      })
    })

    return temp
  }, [data])

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: "0px",
      threshold: 1.0,
    })

    if (observerRef.current) observer.observe(observerRef.current)

    return () => {
      if (observerRef.current) observer.unobserve(observerRef.current)
    }
  }, [observerCallback])

  const groupedNotifications = useMemo(() => {
    const groups: Record<string, any[]> = {}
    notificationList.forEach((notification: any) => {
      const date = new Date(notification.created_at)
      let groupLabel = format(date, "PPP") // Default full date format

      if (isToday(date)) groupLabel = translate("Today")
      else if (isYesterday(date)) groupLabel = translate("Yesterday")

      if (!groups[groupLabel]) groups[groupLabel] = []
      groups[groupLabel].push(notification)
    })

    return groups
  }, [notificationList, translate])

  useEffect(() => {
    if (!isOpen) setNotificationDialog(false)
  }, [isOpen])

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        className={cn(
          "flex w-full flex-col gap-0 rounded-lg p-0 focus:outline-none md:max-w-xl lg:mt-2.5 lg:h-[calc(100vh-20px)]",
          isHebrew ? "rtl lg:ml-2.5" : "ltr lg:mr-2.5",
        )}
        side={sheetSide}
        hideCloseButton={true}
      >
        <SheetHeader className='flex-row items-center justify-between gap-4 space-y-0 border-b border-neutral-100 px-6 py-4'>
          <SheetTitle className='flex items-center gap-4'>
            <p className='font-[inter] text-base font-normal'>{translate("Notifications")}</p>
          </SheetTitle>
          <CloseCircle size={20} className='cursor-pointer' onClick={() => setIsOpen(false)} />
        </SheetHeader>

        <SheetDescription className='flex h-full flex-col overflow-y-auto'>
          <div className='flex items-center justify-between border-b border-neutral-200 px-2'>
            <div className='flex'>
              {notificationTabList.map((tab) => {
                const countKey = unreadCountMap[tab.value] as keyof typeof unreadCounts
                const count = unreadCounts?.[countKey] || 0
                return (
                  <div
                    className={cn(
                      "flex cursor-pointer items-center gap-1.5 px-4 py-3",
                      activeTab === tab.value && "border-b-2 border-neutral-900",
                    )}
                    key={tab.value}
                    onClick={() => setActiveTab(tab.value)}
                  >
                    <p
                      className={cn(
                        "whitespace-nowrap font-[inter] text-sm",
                        activeTab === tab.value
                          ? "font-medium text-neutral-800"
                          : "font-normal text-neutral-600",
                      )}
                    >
                      {translate(tab.label)}
                    </p>
                    {count > 0 && (
                      <span
                        title={String(count)}
                        className='inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-indigo-100 px-1 text-[10px] font-medium leading-none text-indigo-600'
                      >
                        {count > 99 ? "99+" : count}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
            <div className='flex items-center gap-2 px-2 py-3'>
              <label className='whitespace-nowrap font-[inter] text-sm font-normal text-indigo-600'>
                {translate("Unread only")}
              </label>
              <Switch className='h-6 w-11' onCheckedChange={setUnreadOnly} />
            </div>
          </div>

          <div className='flex flex-col gap-4 overflow-y-auto p-4'>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <div className='flex gap-4' key={idx}>
                  <Skeleton circle width={8} height={8} className='mt-3' />
                  <div className='flex w-full flex-col gap-1'>
                    <Skeleton width={"40%"} height={12} />
                    <Skeleton width={"80%"} height={14} />
                  </div>
                  <Skeleton width={50} height={10} />
                </div>
              ))
            ) : notificationList.length === 0 ? (
              <div className='flex h-full flex-col items-center justify-center py-10 text-center text-sm text-neutral-500'>
                {translate("No notifications found")}
              </div>
            ) : (
              Object.entries(groupedNotifications).map(([group, notifications]) => (
                <div key={group} className='flex flex-col gap-4'>
                  <div className='text-[10px] font-semibold text-neutral-600'>{group}</div>
                  {notifications.map((notification) => {
                    const actionText = notification.action_id
                      ? isHebrew
                        ? notification.action_he || translate(notification.action_en)
                        : notification.action_en
                      : isHebrew
                        ? notification.change_type_he || translate(notification.change_type_en)
                        : notification.change_type_en
                    const projectId = notification.project_id
                    const taskId = notification.task_id
                    const isLinkable = projectId && taskId

                    const notificationContent = (
                      <>
                        <div className='flex gap-2.5 py-2.5'>
                          <div
                            className={cn(
                              "h-2 w-2 rounded-full",
                              !notification?._is_read ? "bg-indigo-500" : "bg-white",
                            )}
                          />
                        </div>

                        <div className='flex w-full flex-col gap-0.5'>
                          <div className='font-[inter] text-xs font-normal text-neutral-500'>
                            <span className='flex flex-wrap items-center gap-1'>
                              {notification.bill_id && notification.bill_id !== 0 ? null : notification.by_user_name && (
                                <span className='font-semibold text-neutral-700'>
                                  {notification.by_user_name}
                                </span>
                              )}
                              {actionText && (
                                <span className='font-semibold text-indigo-600'>
                                  {actionText}
                                </span>
                              )}
                              {notification.bill_id && notification.bill_id !== 0 ? (
                                <span className='font-semibold'>
                                  {translate("by")} {notification.by_user_name}
                                </span>
                              ) : notification.action_id ? (
                                <span>
                                  {notification.action_en === "assigned you"
                                    ? translate("a")
                                    : translate("in")}{" "}
                                  {translate("task")}
                                </span>
                              ) : null}
                            </span>
                          </div>
                          <div className='font-[inter] text-sm font-normal text-neutral-800'>
                            <span className='line-clamp-1'>{notification.title || "---"}</span>
                            {notification.project_title && (
                              <span className='text-xs text-neutral-500'>
                                {translate("in")} {translate("project")} {notification.project_title}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className='shrink-0'>
                          <p className='font-[inter] text-xs font-normal text-neutral-500'>
                            {format(new Date(notification.created_at), "HH:mm")}
                          </p>
                        </div>
                      </>
                    )

                    return isLinkable ? (
                      <Link
                        to={`${PROJECT_PAGE_ROUTE}/${projectId}?query=${taskId}`}
                        className='flex cursor-pointer gap-4'
                        key={notification.id}
                        onClick={() => setIsOpen(false)}
                      >
                        {notificationContent}
                      </Link>
                    ) : (
                      <div
                        className='flex gap-4'
                        key={notification.id}
                      >
                        {notificationContent}
                      </div>
                    )
                  })}
                </div>
              ))
            )}

            {/* Observer div for infinite scroll */}
            {hasNextPage && (
              <div ref={observerRef} className='flex flex-col gap-2 pt-4'>
                {isFetchingNextPage && (
                  <div className='col-span-full flex items-center justify-center py-4'>
                    <Skeleton height={20} width={100} />
                  </div>
                )}
              </div>
            )}
          </div>
        </SheetDescription>
      </SheetContent>
    </Sheet>
  )
}

export default NotificationDialog
